import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { checkCallAPI } from '../../api';
import { toast } from 'sonner';
import '../load-workspace/workspace.css';

export const DRIVER_STATUSES = ['On Time', 'Running Late', 'At Facility', 'Loading', 'Unloading', 'Loaded', 'Empty', 'Delayed', 'No Answer'];
export const CONDITIONS = ['Good', 'Delayed', 'At Risk'];
export const CONDITION_COLORS = {
  'Good': { background: '#dcf5e4', border: '#10b759', text: '#0c4a25' },
  'Delayed': { background: '#ffe8d1', border: '#f77e17', text: '#7a3b00' },
  'At Risk': { background: '#ffe0e0', border: '#F8191B', text: '#7a1218' },
};

const CHECKLIST = {
  pickup_confirmation: ['Driver location, ETA, availability & readiness', 'Pickup number, address & instructions'],
  delivery_confirmation: ['Driver location, ETA & readiness', 'Delivery instructions & appointment details'],
  evening_before: ['Driver knows appointment date/time in facility local time', 'Address, check-in instructions & delivery requirements'],
  transit_daily: ['Current location & progress', 'ETA & any reported issues'],
};

const fmtAppt = (item) => {
  if (item.appointment_local_display) {
    return `${item.appointment_date_central || ''} ${item.appointment_local_display}${!item.same_timezone && item.appointment_central_display ? ` (${item.appointment_central_display})` : ''}`.trim();
  }
  if (!item.appointment_datetime) return '—';
  const d = new Date(item.appointment_datetime);
  return isNaN(d.getTime()) ? item.appointment_datetime : d.toLocaleString('en-US', { month: '2-digit', day: '2-digit', hour: 'numeric', minute: '2-digit' }) + ' (facility local)';
};

// Modal to record a structured Check Call for one required task (or an ad-hoc stop check)
export const CheckCallDialog = ({ open, onOpenChange, item, onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm({}); }, [open]);
  if (!item) return null;

  const taskType = item.task_type || (item.stop_type === 'pickup' ? 'pickup_confirmation' : 'delivery_confirmation');
  const isTransit = taskType === 'transit_daily';
  const title = item.task_label || (item.stop_type === 'pickup' ? 'Pickup Check Call' : 'Delivery Check Call');
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const hasTransitInfo = !!(form.location || '').trim() && !!(form.eta || '').trim();

  const save = async () => {
    if (form.counts_as_transit && !hasTransitInfo) { toast.error('Record location and ETA to count this as the daily in-transit update'); return; }
    setSaving(true);
    try {
      const res = await checkCallAPI.create({
        load_id: item.load_id,
        stop_index: isTransit ? null : item.stop_index,
        task_type: taskType,
        task_key: item.task_key || null,
        driver_status: form.driver_status || null,
        condition: form.condition || null,
        eta: form.eta || null,
        location: form.location || null,
        progress: form.progress || null,
        counts_as_transit: !!form.counts_as_transit,
        note: form.note || null,
      });
      toast.success(`${title} saved`);
      onSaved && onSaved(res.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save check call');
    } finally {
      setSaving(false);
    }
  };

  const row = (label, content) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span className="lw-label" style={{ width: 110, fontSize: 12.5 }}>{label}</span>
      <div style={{ flex: 1, minWidth: 0 }}>{content}</div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md lw-root" style={{ background: '#fff' }} data-testid="check-call-modal">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="sr-only">Record a check call</DialogDescription>
        </DialogHeader>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {row('Load', <span style={{ fontWeight: 700, fontFamily: 'monospace' }} data-testid="check-call-load">{item.load_number || item.load_id?.slice(0, 8)}</span>)}
          {!isTransit && row('Stop', <span data-testid="check-call-stop">{item.facility_name || '—'}{item.city ? ` · ${item.city}, ${item.state}` : ''}</span>)}
          {!isTransit && row('Appointment', <span style={{ fontSize: 12.5 }} data-testid="check-call-appt">{fmtAppt(item)}</span>)}
          {item.due_central_display && row('Due', <span style={{ fontSize: 12.5 }}>{item.due_central_display}{item.due_date_central ? ` · ${item.due_date_central}` : ''}</span>)}
          <ul style={{ margin: 0, paddingLeft: 118, fontSize: 11.5, color: 'var(--lw-muted)', listStyle: 'disc' }}>
            {(CHECKLIST[taskType] || []).map(c => <li key={c}>{c}</li>)}
          </ul>
          <div className="lw-sep" />
          {row('Condition',
            <select className="lw-input" value={form.condition || ''} onChange={set('condition')} data-testid="check-call-condition"
              style={form.condition ? { background: CONDITION_COLORS[form.condition].background, borderColor: CONDITION_COLORS[form.condition].border, fontWeight: 700 } : {}}>
              <option value="">Select...</option>
              {CONDITIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
          {row('Driver Status',
            <select className="lw-input" value={form.driver_status || ''} onChange={set('driver_status')} data-testid="check-call-driver-status">
              <option value="">Select...</option>
              {DRIVER_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
          {row('Current Location', <input className="lw-input" value={form.location || ''} onChange={set('location')} placeholder={isTransit ? 'Required for transit update' : 'e.g. San Antonio, TX'} data-testid="check-call-location" />)}
          {row('Current ETA', <input className="lw-input" value={form.eta || ''} onChange={set('eta')} placeholder="e.g. 11:40 AM" data-testid="check-call-eta" />)}
          {isTransit && row('Progress / Issues', <input className="lw-input" value={form.progress || ''} onChange={set('progress')} placeholder="On schedule, no issues" data-testid="check-call-progress" />)}
          {!isTransit && (item.load_status === 'In Transit' || item.allow_transit_link) && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, marginLeft: 118, cursor: 'pointer' }}>
              <input type="checkbox" className="lw-checkbox" checked={!!form.counts_as_transit} onChange={set('counts_as_transit')} data-testid="check-call-counts-as-transit" />
              Also counts as today's in-transit update <span style={{ color: 'var(--lw-muted)' }}>(needs location + ETA)</span>
            </label>
          )}
          {row('Notes',
            <textarea className="lw-input" rows={2} value={form.note || ''} onChange={set('note')} placeholder="Optional — saved to the load's notes" style={{ height: 'auto', padding: '4px 8px' }} data-testid="check-call-note" />
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button className="lw-btn" onClick={() => onOpenChange(false)} disabled={saving} data-testid="check-call-cancel">Cancel</button>
            <button className="lw-btn" onClick={save} disabled={saving} style={{ background: 'var(--lw-red)', borderColor: 'var(--lw-red)', color: '#fff' }} data-testid="check-call-save">
              {saving ? 'Saving…' : 'Save Check Call'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Full check-call timeline for a stop (or a load's transit updates)
export const CheckCallHistoryDialog = ({ open, onOpenChange, loadId, stopIndex, taskKey, title }) => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!open || !loadId) return;
    setItems(null);
    checkCallAPI.history(loadId, stopIndex, taskKey).then(r => setItems(r.data || [])).catch(() => { setItems([]); toast.error('Failed to load history'); });
  }, [open, loadId, stopIndex, taskKey]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg lw-root" style={{ background: '#fff' }} data-testid="check-call-history-modal">
        <DialogHeader>
          <DialogTitle>Check Call History{title ? ` — ${title}` : ''}</DialogTitle>
          <DialogDescription className="sr-only">All check calls recorded</DialogDescription>
        </DialogHeader>
        <div style={{ maxHeight: 380, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items === null && <p style={{ fontSize: 13, color: 'var(--lw-muted)' }}>Loading…</p>}
          {items && items.length === 0 && <p style={{ fontSize: 13, color: 'var(--lw-muted)' }}>No check calls yet.</p>}
          {(items || []).map((c, i) => {
            const cc = c.condition ? CONDITION_COLORS[c.condition] : null;
            return (
              <div key={c.id || i} style={{ border: `1px solid ${cc ? cc.border : 'var(--lw-border)'}`, borderLeftWidth: 4, borderRadius: 4, padding: '6px 10px', background: cc ? cc.background : '#fff' }} data-testid={`check-call-history-item-${i}`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, fontWeight: 700 }}>
                  <span>✓ {c.checked_at_central_display} • {c.checked_by_name}</span>
                  <span style={{ color: 'var(--lw-muted)', fontWeight: 400 }}>{c.task_label || c.stop_type}</span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--lw-muted)' }}>{new Date(c.checked_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                <div style={{ fontSize: 12.5, marginTop: 2 }}>
                  {[c.condition ? `Condition: ${c.condition}` : null, c.driver_status, c.location ? `Location: ${c.location}` : null, c.eta ? `ETA ${c.eta}` : null, c.progress].filter(Boolean).join(' • ') || '—'}
                </div>
                {c.note && <div style={{ fontSize: 12.5, marginTop: 2, whiteSpace: 'pre-wrap' }}>{c.note}</div>}
                {(c.satisfies_task_keys || []).length > 1 && <div style={{ fontSize: 11, color: 'var(--lw-muted)', marginTop: 2 }}>Also satisfied: {c.satisfies_task_keys.filter(k => k !== c.task_key).join(', ')}</div>}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};
